import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore } from "@moss-js/founding-agent/core";

export interface HandoffToastHandle {
  destroy(): void;
}

const JOINED_TEXT = "You are now connected with the team.";
const UNAVAILABLE_TEXT =
  "No human is available right now. The assistant can still help.";

export function createHandoffToast(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
): HandoffToastHandle {
  let toastEl: HTMLDivElement | null = null;

  function render(state: ReturnType<typeof store.getState>): void {
    const { handoffStatus } = state;

    if (handoffStatus === "joined" || handoffStatus === "unavailable") {
      if (!toastEl) {
        toastEl = document.createElement("div");
        toastEl.setAttribute("data-moss-handoff-toast", "");
        toastEl.setAttribute("role", "status");
        toastEl.setAttribute("aria-live", "polite");
        root.appendChild(toastEl);
      }
      toastEl.textContent =
        handoffStatus === "joined" ? JOINED_TEXT : UNAVAILABLE_TEXT;
    } else {
      if (toastEl) {
        toastEl.remove();
        toastEl = null;
      }
    }
  }

  // Gate on handoffStatus: the store fires on every write (audio levels tick
  // ~25Hz during a call) and re-setting textContent would re-announce the toast.
  let prevHandoffStatus = store.getState().handoffStatus;
  render(store.getState());

  const unsub = store.subscribe((state) => {
    if (state.handoffStatus === prevHandoffStatus) return;
    prevHandoffStatus = state.handoffStatus;
    render(state);
  });

  return {
    destroy() {
      unsub();
      toastEl?.remove();
      toastEl = null;
    },
  };
}
